// Phase 10B — Call History page restyled in the AutoMind 2050 design language.
// All API calls (callAPI, callIntelligenceApi, voicemail, sync) and their
// loading/error/empty handling are preserved verbatim — only JSX + classNames
// change. Phone numbers redacted to ***<last4>. Empty/error → EmptyState.

import React, { useState, useEffect } from 'react';
import { Search, Phone, RefreshCw, Voicemail, PhoneOff, Loader2, RotateCw } from 'lucide-react';
import { callAPI } from '../services/api';
import CallRecordingPlayer from '../components/CallRecordingPlayer';
import { PageHeader } from '../components/ui/page-header';
import { DataCard } from '../components/ui/data-card';
import { TabBar } from '../components/ui/tab-bar';
import { SubTabBar } from '../components/ui/sub-tab-bar';
import { StatusPill } from '../components/ui/status-pill';
import { EmptyState } from '../components/ui/empty-state';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../auth/AuthContext';
import { callIntelligenceApi } from '../api';
import CallIntelligencePanel from './CallIntelligence/CallIntelligencePanel';

// Phase 7 — editor roles for re-analyze permission.
const CI_EDITOR_ROLES = new Set([
  'platform_admin',
  'dealer_owner',
  'dealer_manager',
  'admin',
  'owner',
  'manager',
]);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Map call status -> StatusPill tone.
const statusTone = (status) => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'danger';
    case 'no-answer':
    case 'no_answer':
      return 'warning';
    case 'in_progress':
    case 'in-progress':
    case 'queued':
    case 'ringing':
      return 'gold';
    default:
      return 'info';
  }
};

const sentimentTone = (sentiment) => {
  const s = String(sentiment || '').toLowerCase();
  if (s === 'positive') return 'success';
  if (s === 'negative') return 'danger';
  if (s === 'mixed') return 'warning';
  if (s === 'neutral') return 'neutral';
  return 'neutral';
};

// Redact phone numbers to ***<last4>.
const redactPhone = (phone) => {
  if (!phone) return '—';
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length < 4) return '***';
  return `***${digits.slice(-4)}`;
};

const formatTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatDuration = (call) => {
  if (call.duration_ms) return `${Math.round(call.duration_ms / 1000)}s`;
  if (call.duration) return `${Math.round(call.duration)}s`;
  return '—';
};

const CallHistory = () => {
  const [calls, setCalls] = useState([]);
  const [selectedCall, setSelectedCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('transcription');
  const [refreshing, setRefreshing] = useState(false);
  const [processingVoicemail, setProcessingVoicemail] = useState(null);
  const [syncingCall, setSyncingCall] = useState(null);
  const [autoRefresh] = useState(true);
  // Phase 7 — Call Intelligence filter preset + matching call_ids.
  const [ciPreset, setCiPreset] = useState(null);
  const [ciMatchIds, setCiMatchIds] = useState(null); // null = no filter; Set of call_ids otherwise
  const [ciPresetLoading, setCiPresetLoading] = useState(false);
  const { user } = useAuth();
  const isEditor = Boolean(user && CI_EDITOR_ROLES.has(user.role));

  // Check if any calls are in progress (need faster refresh)
  const hasActiveCalls = calls.some((c) =>
    ['ringing', 'in_progress', 'queued'].includes(c.status)
  );
  const refreshInterval = hasActiveCalls ? 10000 : 30000; // 10s for active, 30s otherwise

  useEffect(() => {
    fetchCalls();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchCalls, refreshInterval);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [autoRefresh, refreshInterval]);

  const fetchCalls = async () => {
    try {
      setLoading(true);
      const data = await callAPI.getAll({ limit: 200 }); // Increased to show all calls
      setCalls(Array.isArray(data) ? data : []);
      setLoadError(false);
    } catch (err) {
      console.error('Error fetching calls:', err);
      setLoadError(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchCalls();
  };

  const handleSyncCall = async (callId) => {
    setSyncingCall(callId);
    try {
      const response = await fetch(`${BACKEND_URL}/api/calls/${callId}/sync`, {
        method: 'POST',
      });
      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Call synced successfully');
        await fetchCalls();
        if (selectedCall?.id === callId) {
          const updatedCall = await callAPI.getById(callId);
          setSelectedCall(updatedCall);
        }
      } else {
        toast.error(data.detail || 'Failed to sync call');
      }
    } catch (error) {
      console.error('Error syncing call:', error);
      toast.error('Failed to sync call from Retell');
    } finally {
      setSyncingCall(null);
    }
  };

  // Phase 7 — fetch matching call_ids when a CI preset is active.
  useEffect(() => {
    let cancelled = false;
    const applyPreset = async () => {
      if (!ciPreset) {
        setCiMatchIds(null);
        return;
      }
      const presetParams = {
        HIGH_INTENT: { min_intent_score: 70, limit: 500 },
        REQUESTED_HUMAN: { qa_flag: 'customer_requested_human', limit: 500 },
        QA_FLAGGED: { qa_flag: 'any', limit: 500 },
      }[ciPreset];
      if (!presetParams) {
        setCiMatchIds(null);
        return;
      }
      setCiPresetLoading(true);
      try {
        const res = await callIntelligenceApi.list(presetParams);
        if (cancelled) return;
        const ids = new Set((res?.items || []).map((r) => r.call_id).filter(Boolean));
        setCiMatchIds(ids);
      } catch (err) {
        if (!cancelled) {
          toast.error(err?.message || 'Failed to apply intelligence filter');
          setCiMatchIds(new Set());
        }
      } finally {
        if (!cancelled) setCiPresetLoading(false);
      }
    };
    applyPreset();
    return () => {
      cancelled = true;
    };
  }, [ciPreset]);

  const filteredCalls = calls.filter((call) => {
    const matchesSearch =
      searchQuery === '' ||
      (call.phone_number || '').includes(searchQuery) ||
      (call.id || '').toLowerCase().includes(searchQuery.toLowerCase());
    if (!matchesSearch) return false;

    if (statusFilter !== 'all') {
      if (statusFilter === 'no-answer') {
        if (call.status !== 'no-answer' && call.status !== 'no_answer') return false;
      } else if (call.status !== statusFilter) {
        return false;
      }
    }

    if (ciMatchIds && !ciMatchIds.has(call.id)) return false;
    return true;
  });

  const handleMarkAsVoicemail = async (callId) => {
    setProcessingVoicemail(callId);
    try {
      const response = await fetch(`${BACKEND_URL}/api/voicemail/process/${callId}`, {
        method: 'POST',
      });
      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(
          `Marked as voicemail. Next retry: ${
            data.next_retry ? new Date(data.next_retry).toLocaleString() : 'max retries reached'
          }`
        );
        fetchCalls();
        if (selectedCall?.id === callId) {
          setSelectedCall((prev) => ({
            ...prev,
            voicemail_detected: true,
            call_answered_by: 'voicemail',
          }));
        }
      } else {
        toast.error(data.detail || 'Failed to process voicemail');
      }
    } catch (error) {
      console.error('Error marking voicemail:', error);
      toast.error('Failed to mark as voicemail');
    } finally {
      setProcessingVoicemail(null);
    }
  };

  const detailTabs = [
    { id: 'transcription', label: 'Transcript' },
    { id: 'analysis', label: 'Analysis' },
    { id: 'data', label: 'Data' },
    { id: 'logs', label: 'Logs' },
  ];

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <PageHeader
        eyebrow="Calls"
        title="Call History"
        description="Past calls with searchable transcripts and AI analysis."
        actions={
          <button
            type="button"
            className="btn-2050 btn-secondary"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        }
      />

      <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
        <SubTabBar
          value={statusFilter}
          onChange={setStatusFilter}
          items={[
            { id: 'all', label: 'All' },
            { id: 'completed', label: 'Completed' },
            { id: 'no-answer', label: 'No answer' },
            { id: 'failed', label: 'Failed' },
          ]}
        />
        {/* CI presets */}
        <div
          className="flex items-center gap-1"
          data-testid="ci-filter-presets"
        >
          <button
            type="button"
            className={`btn-2050 ${
              ciPreset === 'HIGH_INTENT' ? 'btn-primary' : 'btn-ghost'
            }`}
            onClick={() => setCiPreset('HIGH_INTENT')}
            disabled={ciPresetLoading}
          >
            High intent
          </button>
          <button
            type="button"
            className={`btn-2050 ${
              ciPreset === 'REQUESTED_HUMAN' ? 'btn-primary' : 'btn-ghost'
            }`}
            onClick={() => setCiPreset('REQUESTED_HUMAN')}
            disabled={ciPresetLoading}
          >
            Requested human
          </button>
          <button
            type="button"
            className={`btn-2050 ${
              ciPreset === 'QA_FLAGGED' ? 'btn-primary' : 'btn-ghost'
            }`}
            onClick={() => setCiPreset('QA_FLAGGED')}
            disabled={ciPresetLoading}
          >
            QA flagged
          </button>
          <button
            type="button"
            className="btn-2050 btn-ghost"
            onClick={() => setCiPreset(null)}
            disabled={ciPresetLoading || !ciPreset}
          >
            Reset
          </button>
        </div>
      </div>

      <div className="mt-4 max-w-sm">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-500" />
          <Input
            placeholder="Search calls…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Call list */}
        <div className="lg:col-span-5">
          <DataCard
            title="Calls"
            description={
              loading
                ? 'Loading…'
                : `${filteredCalls.length} call${filteredCalls.length === 1 ? '' : 's'}`
            }
            loading={loading && calls.length === 0}
            empty={!loading && (loadError || filteredCalls.length === 0)}
            emptyTitle={loadError ? 'No data yet' : 'No calls found'}
            emptyDescription={
              loadError
                ? 'Unable to load right now.'
                : 'Try adjusting your filters or wait for new calls.'
            }
            emptyIcon={Phone}
            bodyClassName={
              !loading && (loadError || filteredCalls.length === 0) ? 'p-6' : 'p-0'
            }
          >
            <div className="overflow-x-auto max-h-[calc(100vh-360px)] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-[0.16em] text-ink-500 bg-ink-50/60 dark:bg-ink-900/40 sticky top-0">
                  <tr>
                    <th className="px-6 py-3 text-left font-medium">Time</th>
                    <th className="px-6 py-3 text-left font-medium">Phone</th>
                    <th className="px-6 py-3 text-left font-medium">Status</th>
                    <th className="px-6 py-3 text-left font-medium">Sentiment</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-200/60 dark:divide-ink-800/60">
                  {filteredCalls.map((call) => {
                    const active = selectedCall?.id === call.id;
                    return (
                      <tr
                        key={call.id}
                        onClick={() => setSelectedCall(call)}
                        data-testid={`call-item-${call.id}`}
                        className={`row-2050 cursor-pointer transition-colors ${
                          active
                            ? 'bg-ink-50 dark:bg-ink-900/60'
                            : 'hover:bg-ink-50/60 dark:hover:bg-ink-900/40'
                        }`}
                      >
                        <td className="px-6 py-3 nums text-ink-500">
                          {formatTime(call.created_at)}
                        </td>
                        <td className="px-6 py-3 nums text-ink-700 dark:text-ink-200">
                          {redactPhone(call.phone_number)}
                        </td>
                        <td className="px-6 py-3">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <StatusPill
                              tone={statusTone(call.status)}
                              dot
                              label={call.status || 'unknown'}
                            />
                            {call.voicemail_detected && (
                              <StatusPill tone="warning" label="voicemail" />
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-3">
                          {call.sentiment ? (
                            <StatusPill
                              tone={sentimentTone(call.sentiment)}
                              label={String(call.sentiment).toLowerCase()}
                            />
                          ) : (
                            <span className="text-ink-500">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </DataCard>
        </div>

        {/* Detail panel */}
        <div className="lg:col-span-7">
          {selectedCall ? (
            <DataCard
              title={redactPhone(selectedCall.phone_number)}
              description={formatTime(selectedCall.created_at)}
              actions={
                <div className="flex items-center gap-2">
                  <StatusPill
                    tone={statusTone(selectedCall.status)}
                    dot
                    label={selectedCall.status || 'unknown'}
                  />
                  {selectedCall.voicemail_detected && (
                    <StatusPill tone="warning" label="voicemail" />
                  )}
                </div>
              }
              bodyClassName="p-0"
            >
              {/* Action bar */}
              <div className="px-6 py-3 border-b border-ink-200/60 dark:border-ink-800/60 flex items-center gap-2 flex-wrap">
                {!selectedCall.voicemail_detected &&
                  selectedCall.status !== 'completed' && (
                    <button
                      type="button"
                      className="btn-2050 btn-ghost"
                      onClick={() => handleMarkAsVoicemail(selectedCall.id)}
                      disabled={processingVoicemail === selectedCall.id}
                      data-testid="mark-voicemail-btn"
                    >
                      {processingVoicemail === selectedCall.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <PhoneOff className="w-4 h-4" />
                      )}
                      Mark as voicemail
                    </button>
                  )}
                {(selectedCall.retell_call_id || selectedCall.elevenlabs_call_id) && (
                  <button
                    type="button"
                    className="btn-2050 btn-ghost"
                    onClick={() => handleSyncCall(selectedCall.id)}
                    disabled={syncingCall === selectedCall.id}
                    data-testid="sync-call-btn"
                  >
                    {syncingCall === selectedCall.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RotateCw className="w-4 h-4" />
                    )}
                    Sync from Retell
                  </button>
                )}
              </div>

              {/* Metrics */}
              <div className="grid grid-cols-4 gap-0 border-b border-ink-200/60 dark:border-ink-800/60">
                <div className="px-6 py-4 border-r border-ink-200/60 dark:border-ink-800/60">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-ink-500 mb-1">
                    Duration
                  </div>
                  <div className="font-display text-lg font-semibold nums">
                    {formatDuration(selectedCall)}
                  </div>
                </div>
                <div className="px-6 py-4 border-r border-ink-200/60 dark:border-ink-800/60">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-ink-500 mb-1">
                    Cost
                  </div>
                  <div className="font-display text-lg font-semibold nums">
                    {selectedCall.cost != null
                      ? `$${Number(selectedCall.cost).toFixed(3)}`
                      : '—'}
                  </div>
                </div>
                <div className="px-6 py-4 border-r border-ink-200/60 dark:border-ink-800/60">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-ink-500 mb-1">
                    Sentiment
                  </div>
                  <div className="text-sm font-medium capitalize">
                    {selectedCall.sentiment || '—'}
                  </div>
                </div>
                <div className="px-6 py-4">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-ink-500 mb-1">
                    Outcome
                  </div>
                  <div className="text-sm font-medium">
                    {selectedCall.outcome || '—'}
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="px-6 pt-2">
                <TabBar
                  value={activeTab}
                  onChange={setActiveTab}
                  items={detailTabs}
                />
              </div>

              {/* Tab content */}
              <div className="p-6 max-h-[calc(100vh-540px)] overflow-y-auto">
                {activeTab === 'transcription' && (
                  <div className="space-y-3">
                    {selectedCall.transcript ? (
                      Array.isArray(selectedCall.transcript) ? (
                        selectedCall.transcript.map((msg, idx) => (
                          <div key={idx} className="flex gap-3">
                            <div className="font-mono text-xs uppercase tracking-wide w-20 shrink-0 text-ink-500 pt-0.5">
                              {msg.speaker || msg.user || 'Speaker'}
                            </div>
                            <div className="text-sm">{msg.text || msg.message}</div>
                          </div>
                        ))
                      ) : (
                        selectedCall.transcript
                          .split('\n')
                          .filter((line) => line.trim())
                          .map((line, idx) => {
                            const match = line.match(
                              /\[(\d+)s\]\s*(AGENT|USER):\s*(.+)/i
                            );
                            if (match) {
                              const [, , speaker, message] = match;
                              return (
                                <div key={idx} className="flex gap-3">
                                  <div className="font-mono text-xs uppercase tracking-wide w-20 shrink-0 text-ink-500 pt-0.5">
                                    {speaker}
                                  </div>
                                  <div className="text-sm">{message}</div>
                                </div>
                              );
                            }
                            return null;
                          })
                      )
                    ) : (
                      <EmptyState
                        title="No transcript"
                        description="Transcript will appear once the call is processed."
                      />
                    )}
                  </div>
                )}

                {activeTab === 'analysis' && (
                  <CallIntelligencePanel
                    callId={selectedCall.id}
                    isEditor={isEditor}
                    onChanged={() => {}}
                  />
                )}

                {activeTab === 'data' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="text-xs text-ink-500 mb-1">Call ID</div>
                        <div className="font-mono text-xs break-all">
                          {selectedCall.id}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-ink-500 mb-1">Retell call ID</div>
                        <div className="font-mono text-xs break-all">
                          {selectedCall.retell_call_id || '—'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-ink-500 mb-1">Lead ID</div>
                        <div className="font-mono text-xs break-all">
                          {selectedCall.lead_id || '—'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-ink-500 mb-1">Campaign ID</div>
                        <div className="font-mono text-xs break-all">
                          {selectedCall.campaign_id || '—'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-ink-500 mb-1">Appointment booked</div>
                        <div className="text-sm">
                          {selectedCall.appointment_booked ? 'Yes' : 'No'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-ink-500 mb-1">Escalation requested</div>
                        <div className="text-sm">
                          {selectedCall.escalation_requested ? 'Yes' : 'No'}
                        </div>
                      </div>
                    </div>
                    {selectedCall.recording_url && (
                      <div className="mt-4">
                        <CallRecordingPlayer call={selectedCall} />
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'logs' && (
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="text-ink-500">Created: </span>
                      <span className="nums">
                        {new Date(selectedCall.created_at).toLocaleString()}
                      </span>
                    </div>
                    <div>
                      <span className="text-ink-500">Updated: </span>
                      <span className="nums">
                        {selectedCall.updated_at
                          ? new Date(selectedCall.updated_at).toLocaleString()
                          : '—'}
                      </span>
                    </div>
                    {selectedCall.disconnect_reason && (
                      <div className="mt-4">
                        <div className="text-xs text-ink-500 mb-2">
                          Disconnect reason
                        </div>
                        <div className="p-3 rounded-lg bg-ink-50 dark:bg-ink-900/60 text-xs font-mono">
                          {selectedCall.disconnect_reason}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </DataCard>
          ) : (
            <DataCard title="Call detail">
              <EmptyState
                title="Select a call"
                description="Pick a row on the left to view transcript, analysis and data."
                icon={Voicemail}
              />
            </DataCard>
          )}
        </div>
      </div>
    </div>
  );
};

export default CallHistory;
